// ============================================================
// context/Navigation.js — which page you're on (the address bar), and moving between pages.
//
// In this file, in order:
//   Going to a page
//   Pages in the address bar
//   Settings in the address (#…)
// ============================================================

import { createContext, useCallback, useContext, useEffect, useState } from "react";

/* ============================================================
   Going to a page
   ============================================================ */

// { page, navigate } for the whole app, provided once in index.js, so
// components don't have to pass `navigate` down through every level.
//   const { page, navigate } = useNavigation();
//   navigate("downloads");                        // push
//   navigate("login", { mode: "create" });        // open the login form's "create" tab
//   navigate("gallery", { replace: true });       // redirect without a history entry
export const NavigationContext = createContext(null);

export function useNavigation() {
  const value = useContext(NavigationContext);
  if (!value) throw new Error("useNavigation must be used inside <NavigationContext.Provider>.");
  return value;
}

/* ============================================================
   Pages in the address bar
   ============================================================ */

// ============================================================
// A minimal client-side router. No external library,
// just the browser's native History API (pushState + popstate).
// Zero app-specific assumptions — copy this one file into any
// other React project unchanged.
//
// HOW TO REUSE THIS IN ANOTHER PROJECT:
//   1. Copy this file as-is into your project.
//   2. Call it with YOUR page names and default:
//        import { useRouter } from "./router";
//        const [page, navigate] = useRouter(["home", "about", "contact"], "home");
//   3. Render whichever page matches `page`, and call `navigate("about")`
//      from any button/link to switch pages — the URL updates to
//      match (/about), and browser back/forward work correctly.
//   4. Production deployment: a static build needs "single-page-app"
//      history-fallback mode (serve -s build with the `serve` package,
//      or the equivalent on Netlify/Vercel/nginx/etc.) so a direct
//      request to e.g. /about serves index.html instead of 404ing.
//      Dev servers (webpack-dev-server, Vite) already do this
//      automatically — this only matters for the production build.
//   5. This only handles PAGE routing (path-based). If you also want
//      to persist some in-page selection (a tab, a selected card) in
//      the URL, append it as extra path segments after the page name
//      (e.g. /gallery/:tileId, /social/:format/:cardId) and read them
//      off window.location.pathname yourself — see useUrlSelection in
//      this project's AppContext.js for a worked example of that
//      pattern alongside this router. This router only ever looks at
//      the first path segment to decide which page is active, so any
//      extra segments after it are yours to use freely.
// ============================================================


export function useRouter(validPages, defaultPage) {
  const pageFromPath = useCallback(
    (pathname) => {
      const cleaned = pathname.replace(/^\/+|\/+$/g, "");
      const firstSegment = cleaned.split("/")[0];
      return validPages.includes(firstSegment) ? firstSegment : defaultPage;
    },
    [validPages, defaultPage]
  );

  const [page, setPageState] = useState(() => pageFromPath(window.location.pathname));

  useEffect(() => {
    function onPopState() {
      setPageState(pageFromPath(window.location.pathname));
    }
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [pageFromPath]);

  // navigate("about")                     -> push /about
  // navigate("about", { replace: true })  -> swap the current history entry
  //                                          (for redirects, so Back doesn't bounce)
  // navigate("gallery", { path: "/gallery/road-closure" }) -> go to a full path
  // The default page lives at "/", not "/<default>".
  const navigate = useCallback(
    (nextPage, { replace = false, path } = {}) => {
      const resolved = validPages.includes(nextPage) ? nextPage : defaultPage;
      const url = path || (resolved === defaultPage ? "/" : "/" + resolved);
      if (url !== window.location.pathname + window.location.search) {
        window.history[replace ? "replaceState" : "pushState"]({}, "", url);
      }
      setPageState(resolved);
      // The same page with a new address (Toolkit from a design, say): parts that read the address hear it.
      window.dispatchEvent(new Event("gsk-navigate"));
    },
    [validPages, defaultPage]
  );

  return [page, navigate];
}

/* ============================================================
   Settings in the address (#…)
   ============================================================ */

// The URL fragment (#…) as lightweight, shareable page state:
//   /help#q=how%20do%20I%20add%20a%20logo      /design#d=…  (see designLink.js)
// Browsers never send the fragment to a server, and we update it with
// replaceState, so it doesn't add history entries.

export function readHash() {
  return new URLSearchParams(window.location.hash.replace(/^#/, ""));
}

export function writeHash(updates) {
  const params = readHash();
  Object.entries(updates).forEach(([key, value]) => {
    if (value === null || value === undefined || value === "") params.delete(key);
    else params.set(key, value);
  });
  const hash = params.toString();
  const url = window.location.pathname + window.location.search + (hash ? `#${hash}` : "");
  if (url !== window.location.pathname + window.location.search + window.location.hash) {
    window.history.replaceState(window.history.state, "", url);
  }
}
